import { useLazyQuery } from '@apollo/client'
import { TableBody, TableCell, TableRow } from '@mui/material'

import { useNavigate } from 'react-router-dom'

import { Flex, FloraTypography } from '@grupoboticario/flora-react'

import {
  getEventStartTime,
  getEventTypeInfo,
  getRegion,
  getTicketsAvailable,
  getTicketsRedeemed
} from '@/components/Table/EventsTable/eventTableHelpers'
import {
  cellTypeStyle,
  cellNameStyle,
  cellRegionStyle,
  cellDateStyle,
  cellRedeemStyle,
  cellTicketsStyle,
  cellCostStyle,
  cellStatusStyle,
  cellActionsStyle
} from '@/components/Table/EventsTable/styles'
import Actions from '@/components/Table/Generic/Actions/Actions'
import EventsStatusTag from '@/components/Table/Generic/EventsStatusTag/EventsStatusTag'
import ScrollableCell from '@/components/Table/Generic/ScrollableCell/ScrollableCell'
import { useUserContext } from '@/context/user'
import { GET_EVENT_BY_ID } from '@/graphql/queries/eventById'
import { EVENTS_STATUS } from '@/pages/benefits/events/constants'
import { Actions as PermissionActions } from '@/utils/dictionaries/actionsDictionary/actions'
import { getFormattedDate } from '@/utils/eventsTable/getFormattedDate'

import * as S from './EventsTableBody.styles'

const ACTION_TYPES = {
  EDIT: 'edit'
}

const EventsTableBody = ({ data }) => {
  const navigate = useNavigate()
  const { hasPermission } = useUserContext()

  const [getEventById] = useLazyQuery(GET_EVENT_BY_ID, {
    fetchPolicy: 'network-only'
  })

  const hasPermissionToEdit = hasPermission(PermissionActions.CREATE_EXCLUSIVE_EVENT)

  return (
    <TableBody>
      {data?.items?.map((row, index) => {
        const eventType = getEventTypeInfo(row?.tag)
        return (
          <TableRow key={row?.id ?? `event-${index}-${row?.name}`}>
            <TableCell style={cellTypeStyle}>
              <Flex align="center" direction="column" gap="2px" justify="center">
                {eventType.icon}
                <FloraTypography css={S.centeredPredominantCss} fontSize="bodySmallStandard">
                  {eventType.label}
                </FloraTypography>
              </Flex>
            </TableCell>
            <TableCell style={cellNameStyle}>
              <ScrollableCell tooltip={row?.name || '-'}>{row?.name || '-'}</ScrollableCell>
            </TableCell>
            <TableCell style={cellRegionStyle}>
              <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard" fontWeight="bold">
                {getRegion(row)}
              </FloraTypography>
            </TableCell>
            <TableCell style={cellDateStyle}>
              <Flex align="flex-start" direction="column" gap="$1">
                <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard">
                  {getFormattedDate(row?.eventDate)}
                </FloraTypography>
                <Flex css={S.dateFlexCss} justify="flex-end">
                  <FloraTypography css={S.auxTextCss} data-testid="startTime" fontSize="bodySmallStandard">
                    {getEventStartTime(row?.startTime)}
                  </FloraTypography>
                </Flex>
              </Flex>
            </TableCell>
            <TableCell style={cellRedeemStyle}>
              <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard">
                {getFormattedDate(row?.redeem?.start)}
              </FloraTypography>
            </TableCell>
            <TableCell style={cellRedeemStyle}>
              <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard">
                {getFormattedDate(row?.redeem?.end)}
              </FloraTypography>
            </TableCell>
            <TableCell style={cellTicketsStyle}>
              <Flex direction="column" gap="$1">
                <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard">
                  {getTicketsAvailable(row?.quantityAvailable)}
                </FloraTypography>
                <FloraTypography css={S.auxTextCss} fontSize="bodySmallStandard">
                  {getTicketsRedeemed(row?.quantityAvailable, row?.quantity)}
                </FloraTypography>
              </Flex>
            </TableCell>
            <TableCell style={cellCostStyle}>
              <FloraTypography css={S.successTextCss} fontSize="bodySmallStandard" fontWeight="bold">
                {row?.benefit?.cost || '-'}
              </FloraTypography>
            </TableCell>
            <TableCell style={cellStatusStyle}>
              <EventsStatusTag status={row?.status || EVENTS_STATUS.INACTIVE} />
            </TableCell>
            <Actions
              actions={{
                edit: hasPermissionToEdit
              }}
              data={row.eventId}
              onActionClick={async (action, eventId) => {
                if (action === ACTION_TYPES.EDIT) {
                  await getEventById({
                    variables: { eventId }
                  })

                  const isReadOnly = row?.status === EVENTS_STATUS.FINISHED
                  navigate(isReadOnly ? `/events/${eventId}?readonly=true` : `/events/${eventId}`)
                }
              }}
              row={row}
              style={cellActionsStyle}
            />
          </TableRow>
        )
      })}
    </TableBody>
  )
}

export default EventsTableBody
